/**
 * ChatCanvas - Canvas Panel for AI-Generated Artifacts
 *
 * A slide-out panel that displays rich content generated by the AI:
 * - Charts (via Recharts)
 * - Data Tables
 * - SQL Queries
 * - Strategy Recommendations
 * - Pinned Metrics
 *
 * Inspired by:
 * - Claude Artifacts
 * - ChatGPT Canvas
 * - Notion AI blocks
 */

import React, { useState } from 'react';
import {
  X,
  Pin,
  PinOff,
  Maximize2,
  Minimize2,
  Download,
  Copy,
  BarChart3,
  Database,
  Code,
  Target,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Play,
  Sparkles,
  CircleDot,
  Video,
  AlertTriangle,
  Activity,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { C } from '../design-system/primitives/constants';
import MCPAppRenderer from './MCPAppRenderer';

// OLED-themed chart colors (gold-accent palette)
const CHART_COLORS = [C.gold, '#A78BFA', '#06B6D4', '#22C55E', '#F97316', '#EF4444'];

// Reusable OLED tooltip style
const TOOLTIP_STYLE = {
  backgroundColor: C.s2,
  border: `1px solid ${C.border}`,
  borderRadius: '8px',
  color: C.text,
};

// Reusable axis/grid style
const AXIS_STROKE = C.t3;
const GRID_STROKE = C.border;

// Artifact type icons
const ARTIFACT_ICONS = {
  chart: BarChart3,
  data_table: Database,
  sql_query: Code,
  strategy: Target,
  metric: TrendingUp,
  shiny: Sparkles, // R Shiny-style interactive visualizations
  video_report: Video, // Video analysis report
  mcp_app: Activity, // MCP Apps interactive visualizations
};

// Pitch type colors (matching R ggplot2 conventions)
const PITCH_COLORS = {
  FF: '#e41a1c', // 4-Seam Fastball - Red
  SI: '#377eb8', // Sinker - Blue
  FC: '#4daf4a', // Cutter - Green
  SL: '#984ea3', // Slider - Purple
  CU: '#ff7f00', // Curveball - Orange
  CH: '#ffff33', // Changeup - Yellow
  FS: '#a65628', // Splitter - Brown
  KC: '#f781bf', // Knuckle Curve - Pink
  KN: '#999999', // Knuckleball - Gray
  default: '#f59e0b', // Amber fallback
};

// Strike zone boundaries (standard MLB)
const STRIKE_ZONE = {
  left: -0.85,
  right: 0.85,
  bottom: 1.5,
  top: 3.5,
};

/**
 * ChartArtifact - Renders chart visualizations
 */
const ChartArtifact = ({ data, onFullscreen }) => {
  const { chartType = 'bar', data: chartData = [], config = {} } = data;
  const { title, xAxis, yAxis } = config;

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48" style={{ color: C.t3 }}>
        No data available
      </div>
    );
  }

  const renderChart = () => {
    const commonProps = { data: chartData, margin: { top: 10, right: 20, left: 0, bottom: 10 } };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis dataKey={xAxis} tick={{ fontSize: 11 }} stroke={AXIS_STROKE} />
            <YAxis tick={{ fontSize: 11 }} stroke={AXIS_STROKE} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend />
            <Line type="monotone" dataKey={yAxis} stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ fill: CHART_COLORS[0] }} />
          </LineChart>
        );

      case 'scatter':
        return (
          <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis
              type="number"
              dataKey={xAxis}
              tick={{ fontSize: 11 }}
              stroke={AXIS_STROKE}
              name={xAxis}
            />
            <YAxis
              type="number"
              dataKey={yAxis}
              tick={{ fontSize: 11 }}
              stroke={AXIS_STROKE}
              name={yAxis}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              contentStyle={TOOLTIP_STYLE}
            />
            <Scatter name={title || 'Data'} data={chartData} fill={CHART_COLORS[0]} />
          </ScatterChart>
        );

      case 'bar':
      default:
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis dataKey={xAxis} tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} height={60} stroke={AXIS_STROKE} />
            <YAxis tick={{ fontSize: 11 }} stroke={AXIS_STROKE} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
            />
            <Bar dataKey={yAxis} radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        );
    }
  };

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

/**
 * DataTableArtifact - Renders data tables
 */
const DataTableArtifact = ({ data }) => {
  const { rows = [], columns = [], title, summary } = data;
  const [showAll, setShowAll] = useState(false);
  const displayedRows = showAll ? rows : rows.slice(0, 5);

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return value % 1 === 0 ? value : value.toFixed(2);
    // Handle Date objects
    if (value instanceof Date) return value.toLocaleDateString();
    // Handle objects (including date-like objects from JSON)
    if (typeof value === 'object') {
      // Check for date-like string properties
      if (value.value) return String(value.value);
      // Try to format as JSON for debugging, but truncate
      try {
        const str = JSON.stringify(value);
        return str.length > 50 ? str.slice(0, 47) + '...' : str;
      } catch {
        return '[Object]';
      }
    }
    return String(value);
  };

  return (
    <div>
      {summary && (
        <p className="text-sm mb-3" style={{ color: C.t2 }}>{summary}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {columns.slice(0, 6).map((col) => (
                <th key={col} className="px-2 py-2 text-left text-xs font-medium uppercase" style={{ color: C.t3 }}>
                  {col.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: `1px solid ${C.borderSubtle}` }}>
                {columns.slice(0, 6).map((col) => (
                  <td key={col} className="px-2 py-2" style={{ color: C.text }}>
                    {formatValue(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 5 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-2 text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
        >
          {showAll ? (
            <>
              <ChevronUp className="w-3 h-3" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" /> Show all {rows.length} rows
            </>
          )}
        </button>
      )}
    </div>
  );
};

/**
 * SQLQueryArtifact - Renders SQL queries with approval
 */
const SQLQueryArtifact = ({ data, onApprove, onReject, onExecute }) => {
  const { generatedSQL, naturalLanguage, status = 'pending', results } = data;
  const [showSQL, setShowSQL] = useState(false);

  const statusConfig = {
    pending: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', icon: null },
    approved: { color: 'text-green-400', bg: 'bg-green-400/10', icon: CheckCircle },
    rejected: { color: 'text-red-400', bg: 'bg-red-400/10', icon: XCircle },
    executed: { color: 'text-amber-400', bg: 'bg-amber-400/10', icon: CheckCircle },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <div>
      {/* Status badge */}
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color} mb-2`}>
        {config.icon && <config.icon className="w-3 h-3" />}
        {status.toUpperCase()}
      </div>

      {/* Natural language description */}
      <p className="text-sm mb-2" style={{ color: C.text }}>{naturalLanguage}</p>

      {/* Toggle SQL */}
      <button
        onClick={() => setShowSQL(!showSQL)}
        className="text-xs flex items-center gap-1 mb-2"
        style={{ color: C.t3 }}
      >
        <Code className="w-3 h-3" />
        {showSQL ? 'Hide SQL' : 'Show SQL'}
      </button>

      {/* SQL Code */}
      {showSQL && (
        <pre className="p-3 rounded-lg text-xs overflow-x-auto mb-3" style={{ background: C.s1, color: C.gold, border: `1px solid ${C.border}` }}>
          <code>{generatedSQL}</code>
        </pre>
      )}

      {/* Results preview */}
      {results && results.length > 0 && (
        <div className="text-xs mb-2" style={{ color: C.t3 }}>
          {results.length} results returned
        </div>
      )}

      {/* Action buttons */}
      {status === 'pending' && (
        <div className="flex gap-2">
          <button
            onClick={() => onApprove?.(data.id)}
            className="flex-1 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-xs font-medium transition-colors"
          >
            <CheckCircle className="w-3 h-3 inline mr-1" />
            Approve
          </button>
          <button
            onClick={() => onReject?.(data.id)}
            className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-xs font-medium transition-colors"
          >
            <XCircle className="w-3 h-3 inline mr-1" />
            Reject
          </button>
        </div>
      )}

      {status === 'approved' && !results && (
        <button
          onClick={() => onExecute?.(data.id)}
          className="w-full px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded text-xs font-medium transition-colors"
        >
          <Play className="w-3 h-3 inline mr-1" />
          Execute Query
        </button>
      )}
    </div>
  );
};

/**
 * ShinyArtifact - R Shiny-style Baseball Visualizations
 *
 * Inspired by Jim Albert's ShinyBaseball package and baseballr best practices.
 * Supports multiple visualization types:
 * - strike_zone: Pitch locations with zone overlay
 * - pitch_movement: Horizontal vs vertical movement (pfx_x, pfx_z)
 * - spray_chart: Batted ball locations on field diagram
 * - velocity_trend: Pitch velocity over time/innings
 * - plate_discipline: O-Swing vs Z-Contact quadrants
 */
const ShinyArtifact = ({ data }) => {
  const {
    vizType = 'strike_zone',
    pitchData = [],
    title = 'Shiny Visualization',
    playerName = '',
    config = {}
  } = data;

  // Get pitch color
  const getPitchColor = (pitchType) => PITCH_COLORS[pitchType] || PITCH_COLORS.default;

  // Strike Zone Visualization (like BrushingZone app)
  const renderStrikeZone = () => {
    if (!pitchData || pitchData.length === 0) {
      return <div className="text-center py-8" style={{ color: C.t3 }}>No pitch data available</div>;
    }

    // Transform data for scatter plot
    const scatterData = pitchData.map((p, i) => ({
      x: p.plate_x || p.px || 0,
      z: p.plate_z || p.pz || 2.5,
      pitchType: p.pitch_type || p.pitch_name || 'FF',
      velocity: p.release_speed || p.velocity || 90,
      result: p.description || p.des || '',
      fill: getPitchColor(p.pitch_type || p.pitch_name),
    }));

    // Group by pitch type for legend
    const pitchTypes = [...new Set(scatterData.map(d => d.pitchType))];

    return (
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis
              type="number"
              dataKey="x"
              domain={[-2, 2]}
              tick={{ fontSize: 10 }}
              stroke={AXIS_STROKE}
              label={{ value: 'Horizontal Location (ft)', position: 'bottom', fontSize: 10, fill: C.t3 }}
            />
            <YAxis
              type="number"
              dataKey="z"
              domain={[0, 5]}
              tick={{ fontSize: 10 }}
              stroke={AXIS_STROKE}
              label={{ value: 'Height (ft)', angle: -90, position: 'left', fontSize: 10, fill: C.t3 }}
            />
            {/* Strike zone rectangle */}
            <ReferenceArea
              x1={STRIKE_ZONE.left}
              x2={STRIKE_ZONE.right}
              y1={STRIKE_ZONE.bottom}
              y2={STRIKE_ZONE.top}
              stroke="#f59e0b"
              strokeWidth={2}
              fill="transparent"
            />
            <Tooltip
              contentStyle={{ ...TOOLTIP_STYLE, fontSize: 11 }}
              formatter={(value, name) => [typeof value === 'number' ? value.toFixed(2) : value, name]}
              labelFormatter={() => ''}
            />
            {pitchTypes.map((pt) => (
              <Scatter
                key={pt}
                name={pt}
                data={scatterData.filter(d => d.pitchType === pt)}
                fill={getPitchColor(pt)}
                opacity={0.7}
              >
                {scatterData.filter(d => d.pitchType === pt).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Scatter>
            ))}
            <Legend
              wrapperStyle={{ fontSize: 10 }}
              formatter={(value) => <span style={{ color: C.t2 }}>{value}</span>}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Pitch Movement Visualization (like Albert's PitchValue app)
  const renderPitchMovement = () => {
    if (!pitchData || pitchData.length === 0) {
      return <div className="text-center py-8" style={{ color: C.t3 }}>No pitch data available</div>;
    }

    // Transform movement data (convert feet to inches, flip x for pitcher POV)
    const movementData = pitchData.map((p) => ({
      x: -(p.pfx_x || 0) * 12, // Flip and convert to inches
      z: (p.pfx_z || 0) * 12,
      pitchType: p.pitch_type || p.pitch_name || 'FF',
      velocity: p.release_speed || p.velocity || 90,
      spinRate: p.release_spin_rate || p.spin_rate || 2200,
      fill: getPitchColor(p.pitch_type || p.pitch_name),
    }));

    const pitchTypes = [...new Set(movementData.map(d => d.pitchType))];

    return (
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis
              type="number"
              dataKey="x"
              domain={[-25, 25]}
              tick={{ fontSize: 10 }}
              stroke={AXIS_STROKE}
              label={{ value: 'Horizontal Break (in)', position: 'bottom', fontSize: 10, fill: C.t3 }}
            />
            <YAxis
              type="number"
              dataKey="z"
              domain={[-25, 25]}
              tick={{ fontSize: 10 }}
              stroke={AXIS_STROKE}
              label={{ value: 'Induced Vertical Break (in)', angle: -90, position: 'left', fontSize: 10, fill: C.t3 }}
            />
            {/* Reference lines at 0 */}
            <ReferenceLine x={0} stroke={C.border} strokeDasharray="3 3" />
            <ReferenceLine y={0} stroke={C.border} strokeDasharray="3 3" />
            <Tooltip
              contentStyle={{ ...TOOLTIP_STYLE, fontSize: 11 }}
              formatter={(value, name) => [typeof value === 'number' ? value.toFixed(1) + ' in' : value, name]}
            />
            {pitchTypes.map((pt) => (
              <Scatter
                key={pt}
                name={pt}
                data={movementData.filter(d => d.pitchType === pt)}
                fill={getPitchColor(pt)}
                opacity={0.7}
              />
            ))}
            <Legend
              wrapperStyle={{ fontSize: 10 }}
              formatter={(value) => <span style={{ color: C.t2 }}>{value}</span>}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Velocity Trend (like career trajectory plots)
  const renderVelocityTrend = () => {
    if (!pitchData || pitchData.length === 0) {
      return <div className="text-center py-8" style={{ color: C.t3 }}>No pitch data available</div>;
    }

    // Group by inning or game and calculate average velocity
    const groupKey = config.groupBy || 'inning';
    const grouped = {};

    pitchData.forEach((p) => {
      const key = p[groupKey] || p.inning || 1;
      if (!grouped[key]) {
        grouped[key] = { total: 0, count: 0 };
      }
      const velo = p.release_speed || p.velocity || 0;
      if (velo > 0) {
        grouped[key].total += velo;
        grouped[key].count += 1;
      }
    });

    const trendData = Object.entries(grouped)
      .map(([key, val]) => ({
        [groupKey]: parseInt(key) || key,
        avgVelocity: val.count > 0 ? val.total / val.count : 0,
      }))
      .sort((a, b) => a[groupKey] - b[groupKey]);

    return (
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis
              dataKey={groupKey}
              tick={{ fontSize: 10 }}
              stroke={AXIS_STROKE}
              label={{ value: groupKey.charAt(0).toUpperCase() + groupKey.slice(1), position: 'bottom', fontSize: 10, fill: C.t3 }}
            />
            <YAxis
              domain={['dataMin - 2', 'dataMax + 2']}
              tick={{ fontSize: 10 }}
              stroke={AXIS_STROKE}
              label={{ value: 'Avg Velocity (mph)', angle: -90, position: 'insideLeft', fontSize: 10, fill: C.t3 }}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value) => [value.toFixed(1) + ' mph', 'Avg Velocity']}
            />
            <Line
              type="monotone"
              dataKey="avgVelocity"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: '#f59e0b', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Plate Discipline Quadrant (like Albert's plate discipline chapter)
  const renderPlateDiscipline = () => {
    const {
      oSwing = 0.30,
      zSwing = 0.65,
      oContact = 0.60,
      zContact = 0.85,
      swingPct = 0.45,
      contactPct = 0.80
    } = data.metrics || {};

    // Create quadrant data
    const quadrantData = [
      { name: playerName || 'Player', oSwing: oSwing * 100, zContact: zContact * 100 }
    ];

    // League averages for reference
    const leagueAvg = { oSwing: 30, zContact: 85 };

    return (
      <div className="space-y-3">
        {/* Summary metrics */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded" style={{ background: C.s2 }}>
            <div className="text-lg font-bold text-amber-400">{(swingPct * 100).toFixed(1)}%</div>
            <div className="text-[10px]" style={{ color: C.t3 }}>Swing%</div>
          </div>
          <div className="p-2 rounded" style={{ background: C.s2 }}>
            <div className="text-lg font-bold text-amber-400">{(contactPct * 100).toFixed(1)}%</div>
            <div className="text-[10px]" style={{ color: C.t3 }}>Contact%</div>
          </div>
          <div className="p-2 rounded" style={{ background: C.s2 }}>
            <div className="text-lg font-bold text-amber-400">{(oSwing * 100).toFixed(1)}%</div>
            <div className="text-[10px]" style={{ color: C.t3 }}>O-Swing%</div>
          </div>
        </div>

        {/* Quadrant chart */}
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              <XAxis
                type="number"
                dataKey="oSwing"
                domain={[15, 45]}
                tick={{ fontSize: 10 }}
                stroke={AXIS_STROKE}
                label={{ value: 'O-Swing%', position: 'bottom', fontSize: 10, fill: C.t3 }}
              />
              <YAxis
                type="number"
                dataKey="zContact"
                domain={[70, 95]}
                tick={{ fontSize: 10 }}
                stroke={AXIS_STROKE}
                label={{ value: 'Z-Contact%', angle: -90, position: 'insideLeft', fontSize: 10, fill: C.t3 }}
              />
              {/* League average reference lines */}
              <ReferenceLine x={leagueAvg.oSwing} stroke={C.t3} strokeDasharray="5 5" />
              <ReferenceLine y={leagueAvg.zContact} stroke={C.t3} strokeDasharray="5 5" />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value) => [value.toFixed(1) + '%', '']}
              />
              <Scatter data={quadrantData} fill="#f59e0b">
                <Cell fill="#f59e0b" />
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="text-[10px] text-center" style={{ color: C.t3 }}>
          Dashed lines = League Average
        </div>
      </div>
    );
  };

  // Spray Chart (basic field visualization)
  const renderSprayChart = () => {
    if (!pitchData || pitchData.length === 0) {
      return <div className="text-center py-8" style={{ color: C.t3 }}>No batted ball data available</div>;
    }

    // Transform hit coordinates (hc_x, hc_y from Statcast)
    const sprayData = pitchData.map((p) => ({
      x: p.hc_x ? (p.hc_x - 125) : (p.fieldX || 0), // Center at 0
      y: p.hc_y ? (200 - p.hc_y) : (p.fieldY || 100), // Flip Y
      result: p.events || p.result || 'out',
      exitVelo: p.launch_speed || p.exit_velocity || 0,
      launchAngle: p.launch_angle || 0,
      fill: p.events?.includes('home_run') || p.result === 'HR' ? '#ef4444' :
            p.events?.includes('single') || p.events?.includes('double') || p.events?.includes('triple') ? '#22c55e' : '#6b7280',
    }));

    return (
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            {/* Field outline (simplified) */}
            <ReferenceLine
              segment={[{ x: -100, y: 0 }, { x: 0, y: 150 }]}
              stroke={C.border}
              strokeWidth={2}
            />
            <ReferenceLine
              segment={[{ x: 100, y: 0 }, { x: 0, y: 150 }]}
              stroke={C.border}
              strokeWidth={2}
            />
            <XAxis type="number" dataKey="x" domain={[-150, 150]} hide />
            <YAxis type="number" dataKey="y" domain={[0, 200]} hide />
            <Tooltip
              contentStyle={{ ...TOOLTIP_STYLE, fontSize: 11 }}
              formatter={(value, name) => {
                if (name === 'exitVelo') return [value.toFixed(1) + ' mph', 'Exit Velo'];
                if (name === 'launchAngle') return [value.toFixed(0) + '°', 'Launch Angle'];
                return [value, name];
              }}
            />
            <Scatter data={sprayData} fill="#f59e0b">
              {sprayData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} opacity={0.7} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        <div className="flex justify-center gap-4 text-[10px] mt-1" style={{ color: C.t3 }}>
          <span className="flex items-center gap-1"><CircleDot className="w-3 h-3 text-red-500" /> HR</span>
          <span className="flex items-center gap-1"><CircleDot className="w-3 h-3 text-green-500" /> Hit</span>
          <span className="flex items-center gap-1"><CircleDot className="w-3 h-3" style={{ color: C.t3 }} /> Out</span>
        </div>
      </div>
    );
  };

  // Render based on vizType
  const renderVisualization = () => {
    switch (vizType) {
      case 'strike_zone':
        return renderStrikeZone();
      case 'pitch_movement':
        return renderPitchMovement();
      case 'velocity_trend':
        return renderVelocityTrend();
      case 'plate_discipline':
        return renderPlateDiscipline();
      case 'spray_chart':
        return renderSprayChart();
      default:
        return renderStrikeZone();
    }
  };

  // State for SQL query visibility
  const [showQuery, setShowQuery] = useState(false);

  // Data story panel component
  const DataStoryPanel = () => {
    if (!data.story || !data.story.headline) return null;

    return (
      <div className="mb-4 p-3 rounded-lg" style={{ background: `${C.gold}08`, border: `1px solid ${C.goldDim}` }}>
        <div className="text-sm font-medium mb-2" style={{ color: C.gold }}>{data.story.headline}</div>
        {data.story.insights && data.story.insights.length > 0 && (
          <ul className="space-y-1">
            {data.story.insights.map((insight, i) => (
              <li key={i} className="text-xs flex items-center gap-2" style={{ color: C.t2 }}>
                <CircleDot className="w-2 h-2 flex-shrink-0" style={{ color: C.gold }} />
                {insight}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  // Source footer with SQL query toggle
  const SourceFooter = () => {
    const queryInfo = data.queryInfo || {};
    const source = queryInfo.source || data.story?.source || 'Statcast';

    return (
      <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between text-xs" style={{ color: C.t3 }}>
          <div className="flex items-center gap-2">
            <Database className="w-3 h-3" />
            <span>Data from: {Array.isArray(source) ? source.join(', ') : source}</span>
          </div>
          {queryInfo.sql && (
            <button
              onClick={() => setShowQuery(!showQuery)}
              className="hover:underline flex items-center gap-1"
              style={{ color: C.gold }}
            >
              <Code className="w-3 h-3" />
              {showQuery ? 'Hide SQL' : 'View SQL'}
            </button>
          )}
        </div>
        {showQuery && queryInfo.sql && (
          <div className="mt-2 p-2 rounded overflow-x-auto" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
            <pre className="text-xs whitespace-pre-wrap font-mono" style={{ color: C.gold }}>
              {queryInfo.sql}
            </pre>
          </div>
        )}
        {queryInfo.explanation && (
          <div className="mt-1 text-xs italic" style={{ color: C.t3 }}>
            {queryInfo.explanation}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Data Story Panel - explains what the visualization shows */}
      <DataStoryPanel />

      {playerName && (
        <div className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          {playerName}
        </div>
      )}
      {title && <div className="text-xs mb-2" style={{ color: C.t2 }}>{title}</div>}

      {/* The visualization */}
      {renderVisualization()}

      {/* Source attribution footer */}
      <SourceFooter />
    </div>
  );
};

/**
 * StrategyArtifact - Renders strategy recommendations
 */
const StrategyArtifact = ({ data }) => {
  const { recommendations = [], summary } = data;

  const priorityColors = {
    high: 'border-red-500/50 bg-red-500/5',
    medium: 'border-yellow-500/50 bg-yellow-500/5',
    low: 'border-green-500/50 bg-green-500/5',
  };

  return (
    <div>
      {summary && (
        <p className="text-sm mb-3" style={{ color: C.text }}>{summary}</p>
      )}
      <div className="space-y-2">
        {recommendations.map((rec, idx) => (
          <div
            key={idx}
            className={`p-3 rounded-lg border ${priorityColors[rec.priority] || ''}`}
            style={!priorityColors[rec.priority] ? { border: `1px solid ${C.border}`, background: C.s2 } : undefined}
          >
            <div className="flex items-start gap-2">
              <span className="text-lg">{rec.priority === 'high' ? '🔥' : rec.priority === 'medium' ? '⚡' : '✓'}</span>
              <div>
                <div className="font-medium text-sm" style={{ color: C.text }}>{rec.title}</div>
                <div className="text-xs mt-1" style={{ color: C.t2 }}>{rec.description}</div>
                {rec.rationale && (
                  <div className="text-xs mt-1 italic" style={{ color: C.t3 }}>{rec.rationale}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * VideoReportArtifact - Renders video analysis report
 * Displays pitch tipping indicators, biomechanics analysis, etc.
 */
const VideoReportArtifact = ({ data }) => {
  const {
    analysis = '',
    analysisType = 'general',
    videoSource = '',
    metadata = {},
    tells = [],
    strengths = [],
    areasForImprovement = [],
    recommendations = [],
    confidenceLevel = 'medium',
  } = data;

  const confidenceColors = {
    high: 'text-green-400 bg-green-500/10',
    medium: 'text-yellow-400 bg-yellow-500/10',
    low: 'text-red-400 bg-red-500/10',
  };

  const analysisTypeLabels = {
    pitch_tipping: 'Advanced Scouting',
    biomechanics: 'Biomechanics Report',
    general: 'Video Analysis',
  };

  return (
    <div className="space-y-4">
      {/* Header with analysis type */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Video className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium" style={{ color: C.text }}>
            {analysisTypeLabels[analysisType] || 'Video Analysis'}
          </span>
        </div>
        {confidenceLevel && (
          <span className={`text-xs px-2 py-1 rounded ${confidenceColors[confidenceLevel]}`}>
            {confidenceLevel.toUpperCase()} confidence
          </span>
        )}
      </div>

      {/* Main analysis text */}
      {analysis && (
        <div className="rounded-lg p-4" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed" style={{ color: C.t2 }}>
            {analysis}
          </pre>
        </div>
      )}

      {/* Pitch Tipping Tells */}
      {tells && tells.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">Advanced Scouting Tells Identified</span>
          </div>
          <div className="space-y-2">
            {tells.map((tell, idx) => (
              <div key={idx} className="bg-red-500/5 border border-red-500/30 rounded-lg p-3">
                <div className="font-medium text-sm text-red-300">{tell.name || tell.type}</div>
                <div className="text-xs mt-1" style={{ color: C.t2 }}>{tell.description}</div>
                {tell.confidence && (
                  <div className="text-xs text-red-400 mt-1">Confidence: {tell.confidence}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Biomechanics Strengths */}
      {strengths && strengths.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">Strengths</span>
          </div>
          <ul className="space-y-1 ml-6">
            {strengths.map((strength, idx) => (
              <li key={idx} className="text-sm list-disc" style={{ color: C.t2 }}>{strength}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Areas for Improvement */}
      {areasForImprovement && areasForImprovement.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-400">Areas for Improvement</span>
          </div>
          <ul className="space-y-1 ml-6">
            {areasForImprovement.map((area, idx) => (
              <li key={idx} className="text-sm list-disc" style={{ color: C.t2 }}>{area}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">Recommendations</span>
          </div>
          <div className="space-y-2">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="bg-blue-500/5 border border-blue-500/30 rounded-lg p-3">
                <div className="text-sm" style={{ color: C.t2 }}>{rec}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata footer */}
      {(videoSource || metadata.timestamp) && (
        <div className="text-xs pt-2" style={{ color: C.t3, borderTop: `1px solid ${C.border}` }}>
          {videoSource && <span>Source: {videoSource}</span>}
          {metadata.timestamp && <span className="ml-2">• {new Date(metadata.timestamp).toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
};

/**
 * ArtifactCard - Container for individual artifacts
 */
const ArtifactCard = ({
  artifact,
  isExpanded,
  onToggle,
  onPin,
  onRemove,
  onApprove,
  onReject,
  onExecute,
}) => {
  const Icon = ARTIFACT_ICONS[artifact.type] || BarChart3;

  const renderContent = () => {
    switch (artifact.type) {
      case 'chart':
        return <ChartArtifact data={artifact.data} />;
      case 'data_table':
        return <DataTableArtifact data={artifact.data} />;
      case 'sql_query':
        return <SQLQueryArtifact data={artifact.data} onApprove={onApprove} onReject={onReject} onExecute={onExecute} />;
      case 'strategy':
        return <StrategyArtifact data={artifact.data} />;
      case 'shiny':
        return <ShinyArtifact data={artifact.data} />;
      case 'video_report':
        return <VideoReportArtifact data={artifact.data} />;
      case 'mcp_app':
        return (
          <MCPAppRenderer
            resourceUri={artifact.resourceUri}
            toolName={artifact.toolName}
            toolInput={artifact.toolInput}
            toolResult={artifact.toolResult}
            height={isExpanded ? 600 : 400}
          />
        );
      default:
        return <pre className="text-xs overflow-x-auto">{JSON.stringify(artifact.data, null, 2)}</pre>;
    }
  };

  return (
    <div
      className="rounded-xl border transition-all duration-200"
      style={{
        border: `1px solid ${artifact.pinned ? 'rgba(232,184,0,0.4)' : C.border}`,
        background: artifact.pinned ? 'rgba(232,184,0,0.04)' : C.s1,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 p-3 cursor-pointer rounded-t-xl transition-colors"
        style={{ '--hover-bg': C.s2 }}
        onMouseEnter={e => e.currentTarget.style.background = C.s2}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        onClick={onToggle}
      >
        <Icon className="w-4 h-4" style={{ color: C.t3 }} />
        <span className="text-sm font-medium flex-1" style={{ color: C.text }}>
          {artifact.title || artifact.type.replace(/_/g, ' ')}
        </span>

        {/* Pin button */}
        <button
          onClick={(e) => { e.stopPropagation(); onPin?.(artifact.id); }}
          className="p-1 rounded"
          style={{ color: artifact.pinned ? C.gold : C.t3 }}
        >
          {artifact.pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
        </button>

        {/* Remove button */}
        <button
          onClick={(e) => { e.stopPropagation(); onRemove?.(artifact.id); }}
          className="p-1 rounded hover:text-red-400"
          style={{ color: C.t3 }}
        >
          <X className="w-3 h-3" />
        </button>

        {/* Expand indicator */}
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" style={{ color: C.t3 }} />
        ) : (
          <ChevronDown className="w-4 h-4" style={{ color: C.t3 }} />
        )}
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-3 pt-0" style={{ borderTop: `1px solid ${C.border}` }}>
          {renderContent()}
        </div>
      )}
    </div>
  );
};

/**
 * Main ChatCanvas Component
 */
export default function ChatCanvas({
  artifacts = [],
  isOpen = false,
  onClose,
  onTogglePin,
  onRemove,
  onApprove,
  onReject,
  onExecute,
  className = '',
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Auto-expand latest artifact
  React.useEffect(() => {
    if (artifacts.length > 0 && !expandedId) {
      setExpandedId(artifacts[artifacts.length - 1].id);
    }
  }, [artifacts, expandedId]);

  // Separate pinned and unpinned
  const pinnedArtifacts = artifacts.filter(a => a.pinned);
  const unpinnedArtifacts = artifacts.filter(a => !a.pinned);

  if (!isOpen) return null;

  return (
    <div
      className={`flex flex-col h-full ${isFullscreen ? 'fixed inset-0 z-50' : 'relative'} ${className}`}
      style={{ background: C.bg, borderLeft: `1px solid ${C.border}` }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
        <h3 className="font-semibold flex items-center gap-2" style={{ color: C.text }}>
          <BarChart3 className="w-4 h-4" style={{ color: C.gold }} />
          Canvas
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 rounded transition-colors"
            style={{ color: C.t3 }}
            onMouseEnter={e => { e.currentTarget.style.background = C.s2; e.currentTarget.style.color = C.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.t3; }}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded transition-colors"
            style={{ color: C.t3 }}
            onMouseEnter={e => { e.currentTarget.style.background = C.s2; e.currentTarget.style.color = C.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.t3; }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Empty state */}
        {artifacts.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center" style={{ color: C.t3 }}>
            <BarChart3 className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-sm">No artifacts yet</p>
            <p className="text-xs mt-1">Charts, tables, and queries will appear here</p>
          </div>
        )}

        {/* Pinned artifacts */}
        {pinnedArtifacts.length > 0 && (
          <div>
            <div className="text-xs font-medium text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Pin className="w-3 h-3" />
              Pinned
            </div>
            <div className="space-y-2">
              {pinnedArtifacts.map((artifact) => (
                <ArtifactCard
                  key={artifact.id}
                  artifact={artifact}
                  isExpanded={expandedId === artifact.id}
                  onToggle={() => setExpandedId(expandedId === artifact.id ? null : artifact.id)}
                  onPin={() => onTogglePin?.(artifact.id)}
                  onRemove={() => onRemove?.(artifact.id)}
                  onApprove={onApprove}
                  onReject={onReject}
                  onExecute={onExecute}
                />
              ))}
            </div>
          </div>
        )}

        {/* Recent artifacts */}
        {unpinnedArtifacts.length > 0 && (
          <div>
            {pinnedArtifacts.length > 0 && (
              <div className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: C.t3 }}>
                Recent
              </div>
            )}
            <div className="space-y-2">
              {unpinnedArtifacts.map((artifact) => (
                <ArtifactCard
                  key={artifact.id}
                  artifact={artifact}
                  isExpanded={expandedId === artifact.id}
                  onToggle={() => setExpandedId(expandedId === artifact.id ? null : artifact.id)}
                  onPin={() => onTogglePin?.(artifact.id)}
                  onRemove={() => onRemove?.(artifact.id)}
                  onApprove={onApprove}
                  onReject={onReject}
                  onExecute={onExecute}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Named exports
export { ChartArtifact, DataTableArtifact, SQLQueryArtifact, StrategyArtifact, ShinyArtifact, VideoReportArtifact, ArtifactCard };
